import { SEMEN_TYPE_OPTION_VALUES } from '@/enums';
import {
  BulkRevisionUpdateInput,
  DonorScheduleSemenNotesDto,
} from '@/services/chute-scheduler';

export const BULK_EDITABLE_FIELDS = [
  'semenId',
  'cane',
  'colCode',
  'naab',
  'sireId',
  'semenType',
  'semenComment',
  'semenLocationId',
  'semenOwnerId',
  'backupSemenId',
  'backupSireId',
  'backupSemenType',
] as const;

export type BulkEditableField = (typeof BULK_EDITABLE_FIELDS)[number];

export type BulkRevisionFormState = {
  semenId: string | null;
  semenName: string | null;
  cane: string | null;
  colCode: string | null;
  naab: string | null;
  sireId: string | null;
  sireName: string | null;
  semenType: number | null;
  semenComment: string | null;
  semenLocationId: string | null;
  semenLocationName: string | null;
  semenOwnerId: string | null;
  semenOwnerName: string | null;
  backupSemenId: string | null;
  backupSemenName: string | null;
  backupSireId: string | null;
  backupSireName: string | null;
  backupSemenType: number | null;
};

const FORM_STATE_KEYS: (keyof BulkRevisionFormState)[] = [
  'semenId',
  'semenName',
  'cane',
  'colCode',
  'naab',
  'sireId',
  'sireName',
  'semenType',
  'semenComment',
  'semenLocationId',
  'semenLocationName',
  'semenOwnerId',
  'semenOwnerName',
  'backupSemenId',
  'backupSemenName',
  'backupSireId',
  'backupSireName',
  'backupSemenType',
];

export interface MergeResult {
  formState: BulkRevisionFormState;
  multipleValueFields: Set<string>;
}

export function mergeFieldValues(
  donorSchedules: DonorScheduleSemenNotesDto[],
): MergeResult {
  const emptyResult: MergeResult = {
    formState: createEmptyFormState(),
    multipleValueFields: new Set(),
  };

  if (donorSchedules.length === 0) {
    return emptyResult;
  }

  if (donorSchedules.length === 1) {
    return {
      formState: mapDtoToFormState(donorSchedules[0]),
      multipleValueFields: new Set(),
    };
  }

  const formState = mapDtoToFormState(donorSchedules[0]);
  const multipleValueFields = new Set<string>();

  for (const key of FORM_STATE_KEYS) {
    const values = donorSchedules.map(
      (ds) => ds[key as keyof DonorScheduleSemenNotesDto],
    );
    const firstValue = values[0];
    const allSame = values.every(
      (v) => v === firstValue || (v == null && firstValue == null),
    );

    if (!allSame) {
      (formState as Record<string, unknown>)[key] = null;
      multipleValueFields.add(key);
    }
  }

  return { formState, multipleValueFields };
}

export function getModifiedFields(
  touchedFields: Set<string>,
  current: BulkRevisionFormState,
): Partial<BulkRevisionUpdateInput> {
  const result: Record<string, unknown> = {};

  for (const field of touchedFields) {
    if (
      BULK_EDITABLE_FIELDS.includes(field as BulkEditableField) &&
      field in current
    ) {
      result[field] = current[field as keyof BulkRevisionFormState];
    }
  }

  return result as Partial<BulkRevisionUpdateInput>;
}

export interface SemenSelectionData {
  id: string;
  name: string | null;
  cane?: string | null;
  colCode?: string | null;
  naab?: string | null;
  sire?: { id: string; name?: string | null } | null;
  sireId?: string | null;
  storageLocation?: { id: string; name?: string | null } | null;
  storageLocationId?: string | null;
  owner?: { id: string; name?: string | null } | null;
  ownerId?: string | null;
  sexed?: boolean | null;
  sex?: number | null;
}

export function calculateSemenType(
  semen: Pick<SemenSelectionData, 'sexed' | 'sex'>,
): number | null {
  if (semen.sexed === false) return null;
  if (semen.sex === 1) return SEMEN_TYPE_OPTION_VALUES.SF_F;
  if (semen.sex === 2) return SEMEN_TYPE_OPTION_VALUES.SF_M;
  return null;
}

export function applySemenSelection(
  semen: SemenSelectionData,
  _currentFormState: BulkRevisionFormState,
): Partial<BulkRevisionFormState> {
  return {
    semenId: semen.id,
    semenName: semen.name,
    cane: semen.cane ?? null,
    colCode: semen.colCode ?? null,
    naab: semen.naab ?? null,
    sireId: semen.sire?.id ?? semen.sireId ?? null,
    sireName: semen.sire?.name ?? null,
    semenType: calculateSemenType(semen),
    semenLocationId:
      semen.storageLocation?.id ?? semen.storageLocationId ?? null,
    semenLocationName: semen.storageLocation?.name ?? null,
    semenOwnerId: semen.owner?.id ?? semen.ownerId ?? null,
    semenOwnerName: semen.owner?.name ?? null,
  };
}

export function applySireSelection(
  sire: { id: string; name: string },
  currentFormState: BulkRevisionFormState,
): Partial<BulkRevisionFormState> {
  const updates: Partial<BulkRevisionFormState> = {
    sireId: sire.id,
    sireName: sire.name,
  };

  if (currentFormState.semenId && currentFormState.sireId !== sire.id) {
    updates.semenId = null;
    updates.semenName = null;
    updates.cane = null;
    updates.colCode = null;
    updates.naab = null;
    updates.semenType = null;
    updates.semenLocationId = null;
    updates.semenLocationName = null;
    updates.semenOwnerId = null;
    updates.semenOwnerName = null;
  }

  return updates;
}

function createEmptyFormState(): BulkRevisionFormState {
  return {
    semenId: null,
    semenName: null,
    cane: null,
    colCode: null,
    naab: null,
    sireId: null,
    sireName: null,
    semenType: null,
    semenComment: null,
    semenLocationId: null,
    semenLocationName: null,
    semenOwnerId: null,
    semenOwnerName: null,
    backupSemenId: null,
    backupSemenName: null,
    backupSireId: null,
    backupSireName: null,
    backupSemenType: null,
  };
}

function mapDtoToFormState(
  ds: DonorScheduleSemenNotesDto,
): BulkRevisionFormState {
  return {
    semenId: ds.semenId,
    semenName: ds.semenName,
    cane: ds.cane,
    colCode: ds.colCode,
    naab: ds.naab,
    sireId: ds.sireId,
    sireName: ds.sireName,
    semenType: ds.semenType,
    semenComment: ds.semenComment,
    semenLocationId: ds.semenLocationId,
    semenLocationName: ds.semenLocationName,
    semenOwnerId: ds.semenOwnerId,
    semenOwnerName: ds.semenOwnerName,
    backupSemenId: ds.backupSemenId,
    backupSemenName: ds.backupSemenName,
    backupSireId: ds.backupSireId,
    backupSireName: ds.backupSireName,
    backupSemenType: ds.backupSemenType,
  };
}
